import { ref, get, set, push, update, runTransaction } from 'firebase/database';
import { db } from '../lib/firebase';
import { FirebaseError } from 'firebase/app';
import { Credit, CreditPurchase, CreditUsage, CreditPackage } from '../types/credits';
import Stripe from 'stripe';

const stripe = new Stripe(import.meta.env.VITE_STRIPE_SECRET_KEY!, {
  apiVersion: '2023-10-16'
});

export const getUserCredits = async (userId: string): Promise<number> => {
  const creditsRef = ref(db, `credits/${userId}`);
  const snapshot = await get(creditsRef);
  
  if (!snapshot.exists()) {
    return 0;
  }

  const credits = Object.values(snapshot.val() as Record<string, Credit>);
  return credits.reduce((total, credit) => total + credit.amount, 0);
};

export const initiatePurchase = async (
  userId: string, 
  quantity: number, 
  amount: number,
  priceId?: string
): Promise<{ sessionId: string; url: string }> => {
  console.log('Démarrage initiatePurchase avec:', { userId, quantity, amount, priceId });
  
  // 1. Créer l'enregistrement d'achat dans Firebase
  const purchasesRef = ref(db, 'credit_purchases');
  const newPurchaseRef = push(purchasesRef);

  const purchase: CreditPurchase = {
    id: newPurchaseRef.key!,
    userId,
    credits: quantity,
    amount: amount,
    paymentMethod: 'stripe',
    status: 'pending',
    createdAt: new Date().toISOString(),
    completedAt: null
  };

  await set(newPurchaseRef, purchase);

  let session;
  console.log('Enregistrement d\'achat créé:', purchase.id);
  
  // 2. Créer la session Stripe avec le priceId si disponible
  if (priceId) {
    console.log('Création de session Stripe avec priceId:', priceId);
    session = await stripe.checkout.sessions.create({
      payment_method_types: ['card'],
      line_items: [{
        price: priceId,
        quantity: 1
      }],
      mode: 'payment',
      allow_promotion_codes: true,
      success_url: `${window.location.origin}/credits/confirmation?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${window.location.origin}/credits/purchase`,
      metadata: {
        userId,
        purchaseId: newPurchaseRef.key!,
        credits: quantity.toString(),
        amount: amount.toString()
      }
    });
  } else {
    console.log('Création de session Stripe sans priceId (fallback)');
    // Fallback au cas où le priceId n'est pas fourni
    session = await stripe.checkout.sessions.create({
      payment_method_types: ['card'],
      line_items: [{
        price_data: {
          currency: 'eur',
          product_data: {
            name: `Pack de ${quantity} crédit${quantity > 1 ? 's' : ''} SimulReno`,
            description: `${quantity} crédit${quantity > 1 ? 's' : ''} pour des estimations de travaux détaillées`,
          },
          unit_amount: Math.round(amount * 100 / quantity), // Prix par crédit en centimes
        },
        quantity: quantity,
      }],
      mode: 'payment',
      allow_promotion_codes: true,
      success_url: `${window.location.origin}/credits/confirmation?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${window.location.origin}/credits/purchase`,
      metadata: {
        userId,
        purchaseId: newPurchaseRef.key!,
        credits: quantity.toString(),
        amount: amount.toString()
      }
    });
  }
  
  console.log('Session Stripe créée:', { 
    id: session.id, 
    url: session.url,
    status: session.status
  });

  return {
    sessionId: session.id,
    url: session.url!
  };
};

export const checkPurchaseStatus = async (sessionId: string): Promise<string> => {
  try {
    const session = await stripe.checkout.sessions.retrieve(sessionId);

    if (session.payment_status === 'paid') {
      // Récupérer les métadonnées
      const { userId, purchaseId } = session.metadata;

      // Mettre à jour l'achat et ajouter les crédits (utiliser les valeurs des métadonnées)
      const credits = parseInt(session.metadata.credits || '1');
      const amount = parseFloat(session.metadata.amount || '49.90');
      await completePurchase(userId, purchaseId, credits, amount);
      return 'completed';
    }
    
    if (session.payment_status === 'unpaid') {
      return 'failed';
    }
    
    return 'pending';
  } catch (err) {
    console.error('Erreur Stripe:', err);
    throw new Error('Erreur lors de la vérification du paiement');
  }
};

export const completePurchase = async (
  userId: string, 
  purchaseId: string, 
  credits?: number, 
  amount?: number
): Promise<void> => {
  try {
    console.log('Tentative de complétion de l\'achat...', { userId, purchaseId, credits, amount });
    
    // 1. Vérifier et mettre à jour l'achat
    const purchaseRef = ref(db, `credit_purchases/${purchaseId}`);
    const purchaseSnapshot = await get(purchaseRef);
    
    if (!purchaseSnapshot.exists()) {
      throw new Error('Purchase not found');
    }

    const purchase = purchaseSnapshot.val() as CreditPurchase;
    
    if (purchase.userId !== userId) {
      throw new Error('Unauthorized');
    }
    
    if (purchase.status === 'completed') {
      return;
    }
    
    // Utiliser les valeurs passées en paramètre ou celles de l'achat
    const finalCredits = credits || purchase.credits;
    const finalAmount = amount || purchase.amount;

    // 2. Mettre à jour le statut d'achat
    await set(purchaseRef, {
      ...purchase,
      credits: finalCredits,
      amount: finalAmount,
      status: 'completed',
      completedAt: new Date().toISOString()
    });

    // 3. Ajouter les crédits dans une opération séparée
    const newCreditRef = push(ref(db, `credits/${userId}`));
    await set(newCreditRef, {
      userId,
      amount: finalCredits,
      createdAt: new Date().toISOString(),
      purchaseId // Important: ajouter le purchaseId pour respecter la validation
    });

  } catch (error) {
    console.error('Erreur lors de la complétion de l\'achat:', error);
    throw new Error('Erreur lors de la mise à jour du compte');
  }
};

export const getCreditHistory = async (userId: string): Promise<CreditPurchase[]> => {
  try {
    const purchasesRef = ref(db, 'credit_purchases');
    const snapshot = await get(purchasesRef);
    
    if (!snapshot.exists()) {
      return [];
    }

    // Filtrer pour ne récupérer que les transactions de l'utilisateur
    // et trier par date décroissante
    return Object.entries(snapshot.val())
      .map(([id, data]) => ({
        id,
        ...(data as Omit<CreditPurchase, 'id'>)
      }))
      .filter(purchase => purchase.userId === userId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'historique:', error);
    throw new Error('Impossible de récupérer l\'historique des achats');
  }
};

/**
 * Consomme un nombre spécifié de crédits pour un utilisateur
 * @param userId ID de l'utilisateur
 * @param quantity Nombre de crédits à consommer
 * @param metadata Métadonnées optionnelles sur l'utilisation (type d'opération, etc.)
 * @returns Promise<void>
 * @throws Error si le solde est insuffisant ou en cas d'erreur de transaction
 *
 * Consomme un nombre spécifié de crédits pour un utilisateur
 * @param userId ID de l'utilisateur
 * @param quantity Nombre de crédits à consommer
 * @param metadata Métadonnées optionnelles sur l'utilisation
 * @throws {Error} Erreur détaillée selon le type d'erreur rencontré
 */
export const consumeCredits = async (
  userId: string,
  quantity: number,
  metadata?: {
    estimateId?: string;
    operationType?: string;
    address?: string;
  }
): Promise<void> => {
  if (!userId) throw new Error('ID utilisateur requis');
  if (quantity <= 0) throw new Error('La quantité doit être supérieure à 0');

  const creditsRef = ref(db, `credits/${userId}`);

  try {
    // Vérifier le solde initial
    const snapshot = await get(creditsRef);
    const currentCredits = snapshot.val();

    console.log('État initial des crédits:', {
      raw: currentCredits,
      parsed: JSON.stringify(currentCredits, null, 2),
    });

    // Calculer le solde total
    const totalCredits = currentCredits
      ? Object.values(currentCredits).reduce(
          (sum: number, credit: any) => sum + (credit.amount || 0),
          0
        )
      : 0;

    console.log('Vérification du solde:', {
      disponible: totalCredits,
      demandé: quantity,
    });

    if (totalCredits < quantity) {
      throw new Error('INSUFFICIENT_CREDITS');
    }

    // Exécuter une transaction pour décrémenter les crédits
    await runTransaction(creditsRef, (credits) => {
      if (!credits) return {};

      let remainingToDeduct = quantity;
      const updatedCredits = { ...credits };

      for (const [id, credit] of Object.entries(updatedCredits)) {
        if (remainingToDeduct <= 0) break;

        const currentAmount = (credit as Credit).amount || 0;
        if (typeof currentAmount !== 'number' || currentAmount < 0) {
          console.error(`[consumeCredits] Crédit invalide détecté pour ${id}:`, credit);
          continue;
        }

        const deduction = Math.min(currentAmount, remainingToDeduct);
        console.log(`[consumeCredits] Déduction ${deduction} sur le crédit ${id}`);

        updatedCredits[id] = {
          ...(credit as Credit),
          amount: currentAmount - deduction,
          updatedAt: new Date().toISOString(),
        };

        // Optionnel : Supprimer les crédits épuisés pour garder une base propre
        if (updatedCredits[id].amount === 0) {
          delete updatedCredits[id];
          console.log(`[consumeCredits] Crédit ${id} supprimé (épuisé).`);
        }

        remainingToDeduct -= deduction;
      }

      console.log('Mise à jour des crédits après transaction:', {
        avant: credits,
        après: updatedCredits,
      });

      return updatedCredits;
    });

    // Enregistrer l'utilisation du crédit
    if (metadata) {
      const usageRef = push(ref(db, `credit_usage/${userId}`));
      const usage: CreditUsage = {
        id: usageRef.key!,
        userId,
        quantity,
        usedAt: new Date().toISOString(),
        ...metadata,
      };

      await set(usageRef, usage);
      console.log("Enregistrement de l'utilisation des credits:", usage);
    }
  } catch (error: any) {
    console.error('Erreur lors de la consommation des crédits:', {
      error,
      type: error.constructor.name,
      message: error.message,
      stack: error.stack,
    });

    // Gestion des erreurs Firebase spécifiques
    if (error instanceof FirebaseError) {
      switch (error.code) {
        case 'PERMISSION_DENIED':
          throw new Error('Accès refusé aux crédits');
        case 'DISCONNECTED':
          throw new Error('Erreur de connexion');
        default:
          throw new Error(`Erreur Firebase: ${error.message}`);
      }
    }

    // Gestion des erreurs personnalisées
    if (error.message === 'INSUFFICIENT_CREDITS') {
      throw new Error('Solde de crédits insuffisant');
    }

    // Erreur par défaut
    throw new Error('Erreur lors de la mise à jour des crédits');
  }
};